import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Client } from "assets/SideBarIcons/customer.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import {
  ClientProfileService,
  CommonProfileService,
  UserModuleProfileService,
} from "subModule/src/core/services";
import { DeleteData } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetClientsService } from "subModule/src/services/client/useClient";
import {
  ClientResponse,
  ChangeStatusClientResponse,
} from "subModule/src/services/client/interface";
import ClientSearchHeader from "components/Client/ClientSearchHeader";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import ClientSearchHeaderMenu from "components/Client/ClientSearchHeaderMenu";
import DropdownMenu from "common/DropDownMenu/DropDownMenu";

/** CUSTOMER_RETENTION_STATUS: 1=ACTIVE, 2=LOST, 3=FOLLOWUP, 4=AT_RISK */
const RETENTION_LOST = 2;
const RETENTION_FOLLOW_UP = 3;
const RETENTION_AT_RISK = 4;
/** WhatsApp templates: 7=CUSTOMER_FOLLOW_UP, 8=CUSTOMER_LOST, 9=CUSTOMER_AT_RISK */
const WHATSAPP_TEMPLATE_BY_RETENTION: Record<number, number> = {
  [RETENTION_FOLLOW_UP]: 7,
  [RETENTION_LOST]: 8,
  [RETENTION_AT_RISK]: 9,
};

interface SearchFormData {
  [key: string]: string | number | number[] | null;
}
export default function ClientsListing() {
  const navigate = useNavigate();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusModal, setStatusModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["name", "type", "source"]);
  const [search, setSearch] = useState<any>({
    type: [160, 170, 180],
    source: null,
  });
  const [row, setRow] = useState<ClientResponse>({});
  const [downloading, setDownloading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const loggedInUserData = useSelector(selectUserInfo);
  const loggedInUserType = loggedInUserData.type;
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    name: "",
    ClientName: "",
    accountTitle: "",
    contractNumber: "",
    type: [160, 170, 180],
    source: null,
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetClientsService(
    { pageNumber: pageNo, size: pageSize, ...search },
    loggedInUserType === -1 ? "clients/contractclient" : "clients",
  );
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const ClientTypeList = [
    { value: [160, 170, 180], label: t("all") },
    { value: [170, 180], label: t("Individual") },
    { value: [160], label: t("company") },
  ];
  const sourceList = [
    { label: t("Walk-in"), value: 1 },
    { label: t("Phone / WhatsApp"), value: 2 },
    { label: t("Online"), value: 3 },
    { label: t("Referral"), value: 4 },
    { label: t("Insurance"), value: 5 },
    { label: t("Corporate / Fleet"), value: 6 },
    { label: t("Returning Customer"), value: 7 },
    { label: t("OTHER"), value: 8 },
  ];
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.type]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.source]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => {
    const canManageContract =
      loggedInUserType === -1 || loggedInUserType === 100;
    const isContractClient =
      canManageContract && Boolean(row?.account?.contractClient);
    if (row.type === 160) {
      sessionStorage.setItem(
        `client-contractClient-${row.id}`,
        String(isContractClient),
      );
      navigate(
        "/client/" +
          row.id +
          "/account/" +
          row?.account?.id +
          "/invoicingPreference/" +
          row?.account?.invoicingPreference,
        { state: { contractClient: isContractClient } },
      );
    } else navigate("/customer/" + row.id);
  };
  const showRowView = (row: any) => {
    const canManageContract =
      loggedInUserType === -1 || loggedInUserType === 100;
    const isContractClient =
      canManageContract && Boolean(row?.account?.contractClient);
    if (row.type === 160) {
      sessionStorage.setItem(
        `client-contractClient-${row.id}`,
        String(isContractClient),
      );
      navigate(
        "/client/" +
          row.id +
          "/account/" +
          row?.account?.id +
          "/invoicingPreference/" +
          row?.account?.invoicingPreference,
        { state: { contractClient: isContractClient } },
      );
    } else navigate("/customer/" + row.id);
  };
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["name", "type", "source"]);
    setSearchFormData({
      name: "",
      ClientName: "",
      accountTitle: "",
      contractNumber: "",
      type: [160, 170, 180],
      source: null,
    });
    setSearch({ type: [160, 170, 180], source: null });
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = field === "type" || field === "source" ? value?.value : value;
    if (
      field === "type" &&
      JSON.stringify(searchFormData.type) === JSON.stringify(val)
    )
      return;
    if (field === "source" && searchFormData.source === val) return;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
    if (field === "type") setLoading(true);
    if (field === "source") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | number[]> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const getClientRetentionStatus = (row: any) =>
    Number(row?.status ?? row?.retentionStatus ?? 0);

  const onSendWhatsapp = (row: any) => {
    const clientName = (row?.name || row?.account?.title || "").trim();
    const clientPhone = (row?.phone || row?.account?.phone || "").trim();
    const retentionStatus = getClientRetentionStatus(row);
    const template = WHATSAPP_TEMPLATE_BY_RETENTION[retentionStatus];
    if (!template) return;
    if (!clientName || !clientPhone) {
      enqueueSnackbar(
        <AlertMessages
          text={
            !clientName
              ? t("name") + " " + t("This field is required.")
              : t("phoneRequired")
          }
        />,
        { variant: "error" },
      );
      return;
    }
    UserModuleProfileService.sendClientWhatsappTemplate({
      template,
      clientName,
      clientPhone,
    })
      .then((response: any) => {
        if (response?.success !== false) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response?.status}
              text={t("whatsappSentSuccessfully")}
            />,
            { variant: "success" },
          );
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={t(response?.message) || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
    {
      label: "sendWhatsapp",
      onClick: onSendWhatsapp,
      icon: <WhatsAppIcon sx={{ color: "#25D366" }} />,
      show: (row: any) =>
        WHATSAPP_TEMPLATE_BY_RETENTION[getClientRetentionStatus(row)] != null,
    },
  ];
  const adjustActions = (actions: any[]) => {
    let filtered = actions;
    if (!checkPrivileges("ROLE_CLIENT_UPDATE")) {
      filtered = filtered.filter((action) => action.label !== "edit");
    }
    if (!checkPrivileges("ROLE_CLIENT_ARCHIVE")) {
      filtered = filtered.filter((action) => action.label !== "delete");
    }
    return filtered;
  };
  const DownloadClients = () => {
    const params: any = new URLSearchParams();

    Object.entries(search).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          value.forEach((val) => params.append(key, String(val)));
        } else {
          params.append(key, String(value));
        }
      }
    });
    CommonProfileService.exportEntityWithParams(params, "clients/export/excel")
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Customers.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={(event?: React.MouseEvent<HTMLButtonElement>) =>
          setAnchorEl(
            (event as React.MouseEvent<HTMLButtonElement>).currentTarget,
          )
        }
        heading={t("customers")}
        icon={<Client height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_CLIENT_CREATE")}
      />
      <DropdownMenu
        anchorEl={anchorEl}
        menuItems={[
          {
            label: t("Customer") + " (" + t("Individual") + ")",
            onClick: () => navigate("/customer/create"),
          },
          {
            label: t("Customer") + " (" + t("Company") + ")",
            onClick: () => navigate("/client/create"),
          },
        ]}
        onClose={() => setAnchorEl(null)}
        hoverColor={themeColors?.primary}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            {
              id: "clientIdentity",
              label: "customerCompany",
              numeric: false,
              width: "16%",
            },
            ...(loggedInUserType === -1 || loggedInUserType === 100
              ? [
                  {
                    id: "businessName",
                    label: "businessName",
                    numeric: false,
                    width: "10%",
                  },
                ]
              : []),
            {
              id: "rating",
              label: "rating",
              numeric: false,
              width: "12%",
            },
            {
              id: "lastVisitDate",
              label: "lastVisit",
              numeric: false,
              width: "10%",
            },
            {
              id: "clientRetention",
              label: "retention",
              numeric: false,
              width: "10%",
            },
            {
              id: "clientTypeSource",
              label: "typeSource",
              numeric: false,
              width: "12%",
            },
            {
              id: "contact",
              label: "contact",
              numeric: false,
              width: "14%",
            },
            ...(checkPrivileges("ROLE_CLIENT_ACTIVE")
              ? [
                  {
                    id: "active",
                    label: "status",
                    numeric: false,
                    width: "8%",
                  },
                ]
              : []),
            ...(adjustActions(actionslist).length
              ? [
                  {
                    id: "actions",
                    label: "actions",
                    numeric: false,
                    width: "5%",
                  },
                ]
              : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          onChangeStatus={onChangeStatus}
          rowClick={checkPrivileges("ROLE_CLIENT_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          headerComponent={
            <>
              <Box className="table-header-search">
                <ClientSearchHeader
                  searchFormData={searchFormData}
                  handleChangeFilter={handleChangeFilter}
                  handleChangeSearch={handleChangeSearch}
                  handleSubmitSearch={handleSubmitSearch}
                  clearSearch={clearSearch}
                  {...{ filter, search, ClientTypeList, sourceList }}
                />
              </Box>
              {checkPrivileges("ROLE_CLIENT_EXPORT") ||
              checkPrivileges("ROLE_CLIENT_IMPORT") ? (
                <Box className="table-header-actions">
                  {checkPrivileges("ROLE_CLIENT_EXPORT") ? (
                    <Button
                      id="import-button"
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        setDownloading(true);
                        DownloadClients();
                      }}
                      className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
                      sx={{
                        outline: "2px solid",
                        color: themeColors?.secondary,
                        borderColor: themeColors?.secondary,
                        height: 34,
                        minHeight: 34,
                      }}
                      disabled={downloading}
                    >
                      {downloading ? (
                        <CircularProgress
                          sx={{
                            color: themeColors?.primary,
                            marginRight: "8px",
                          }}
                          size={16}
                        />
                      ) : null}
                      {t("export")}
                    </Button>
                  ) : null}
                  {checkPrivileges("ROLE_CLIENT_IMPORT") ? (
                    <ClientSearchHeaderMenu refetch={refetch} />
                  ) : null}
                </Box>
              ) : null}
            </>
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("Customer")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            ClientProfileService.deleteClient(
              (row as any).id,
              row.type === 160 ? "clients/archive" : "customers/archive",
            )
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("Customer") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(<AlertMessages text={response.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            ClientProfileService.changeStatusClient(
              (row as any).id,
              row.active ? "inactive" : "active",
              { typeName: "clients" },
            )
              .then((response: ChangeStatusClientResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(<AlertMessages text={response.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
